import React from 'react';
import { getResponsiveMultiplier } from '../../utils/visualizerUtils';

// Particle system for cosmic effects
let particles = [];

const createParticle = (x, y, intensity) => {
  return {
    x: x,
    y: y,
    vx: (Math.random() - 0.5) * 4,
    vy: (Math.random() - 0.5) * 4 - 2,
    life: 1.0,
    decay: 0.02 + Math.random() * 0.02,
    size: 2 + Math.random() * 3,
    hue: Math.random() * 360,
    intensity: intensity / 255
  };
};

const updateParticles = () => {
  for (let i = particles.length - 1; i >= 0; i--) {
    const particle = particles[i];
    
    particle.x += particle.vx;
    particle.y += particle.vy;
    particle.life -= particle.decay;
    particle.vy += 0.1; // Gravity effect
    
    if (particle.life <= 0) {
      particles.splice(i, 1);
    }
  }
};

const drawParticles = (ctx) => {
  particles.forEach(particle => {
    const alpha = particle.life * particle.intensity;
    
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle = `hsla(${particle.hue}, 80%, 70%, ${alpha})`;
    ctx.beginPath();
    ctx.arc(particle.x, particle.y, particle.size * particle.life, 0, Math.PI * 2);
    ctx.fill();
    
    // Add glow effect
    ctx.shadowBlur = 10;
    ctx.shadowColor = `hsla(${particle.hue}, 80%, 70%, ${alpha * 0.5})`;
    ctx.fill();
    ctx.restore();
  });
};

export const drawCosmicWaves = (ctx, dataArray, width, height, time) => {
  if (!ctx || !dataArray || width <= 0 || height <= 0) return;

  try {
    // Clear canvas with fade effect for trailing
    ctx.fillStyle = 'rgba(0, 0, 0, 0.1)';
    ctx.fillRect(0, 0, width, height);

    // Create gradient background
    const gradient = ctx.createRadialGradient(width/2, height/2, 0, width/2, height/2, width/2);
    gradient.addColorStop(0, 'rgba(147, 51, 234, 0.1)');
    gradient.addColorStop(1, 'rgba(0, 0, 0, 0.8)');
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, width, height);

    // Draw frequency bars using optimized approach (reduced bar count for performance)
    const bufferLength = dataArray.length;
    const maxBars = Math.min(200, bufferLength); // Increased from 120 to 200 (22% reduction)
    const step = Math.floor(bufferLength / maxBars);
    const barWidth = width / maxBars * getResponsiveMultiplier(); // Use responsive multiplier
    let x = 0;

    for (let i = 0; i < maxBars; i++) {
      const dataIndex = Math.min(i * step, bufferLength - 1);
      const barHeight = (dataArray[dataIndex] / 255) * height * 0.8;

      // Create bar gradient
      const barGradient = ctx.createLinearGradient(0, height, 0, height - barHeight);
      const hue = (i / maxBars) * 360;
      barGradient.addColorStop(0, `hsla(${hue + 280}, 80%, 60%, 0.8)`);
      barGradient.addColorStop(1, `hsla(${hue + 320}, 90%, 70%, 1)`);

      ctx.fillStyle = barGradient;
      ctx.fillRect(x, height - barHeight, barWidth, barHeight);

      // Create particles for intense frequencies
      if (dataArray[i] > 150 && Math.random() > 0.7) {
        particles.push(createParticle(x + barWidth/2, height - barHeight, dataArray[i]));
      }

      x += barWidth + 1; // Small gap between bars
    }

    // Draw circular visualizer in center
    const centerX = width / 2;
    const centerY = height / 2;
    const radius = Math.min(width, height) * 0.15;

    // Draw base circle
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.3)';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, Math.PI * 2);
    ctx.stroke();

    // Draw radial frequency spikes
    for (let i = 0; i < bufferLength; i += 2) {
      const angle = (i / bufferLength) * Math.PI * 2;
      const amplitude = (dataArray[i] / 255) * 50;

      const x1 = centerX + Math.cos(angle) * radius;
      const y1 = centerY + Math.sin(angle) * radius;
      const x2 = centerX + Math.cos(angle) * (radius + amplitude);
      const y2 = centerY + Math.sin(angle) * (radius + amplitude);

      const hue = (i / bufferLength) * 360 + 200;
      const alpha = amplitude / 50;
      
      ctx.strokeStyle = `hsla(${hue}, 80%, 60%, ${alpha})`;
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();

      // Add glow effect for intense spikes
      if (amplitude > 25) {
        ctx.save();
        ctx.shadowBlur = 15;
        ctx.shadowColor = `hsla(${hue}, 80%, 60%, ${alpha * 0.5})`;
        ctx.stroke();
        ctx.restore();
      }
    }

    // Update and draw particles
    updateParticles();
    drawParticles(ctx);

    // Add cosmic wave effects
    drawCosmicWaveEffects(ctx, dataArray, width, height, time);

  } catch (error) {
    console.error('❌ Cosmic Waves visualization error:', error);
  }
};

const drawCosmicWaveEffects = (ctx, dataArray, width, height, time) => {
  // Calculate average intensity for wave effects
  const avgIntensity = dataArray.reduce((sum, val) => sum + val, 0) / dataArray.length;
  const normalizedIntensity = avgIntensity / 255;

  // Draw flowing cosmic waves
  const waveCount = 3;
  for (let w = 0; w < waveCount; w++) {
    ctx.save();
    
    const waveOffset = (w / waveCount) * Math.PI * 2;
    const waveSpeed = 0.5 + w * 0.2;
    const waveAmplitude = 30 + normalizedIntensity * 40;
    const waveFrequency = 0.01 + w * 0.005;
    
    ctx.strokeStyle = `hsla(${180 + w * 60}, 70%, 60%, ${0.3 + normalizedIntensity * 0.4})`;
    ctx.lineWidth = 2 + normalizedIntensity * 3;
    
    ctx.beginPath();
    for (let x = 0; x <= width; x += 2) {
      const y = height/2 + 
                Math.sin(x * waveFrequency + time * waveSpeed + waveOffset) * waveAmplitude +
                Math.sin(x * waveFrequency * 2 + time * waveSpeed * 1.5) * waveAmplitude * 0.5;
      
      if (x === 0) {
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
      }
    }
    ctx.stroke();
    
    // Add glow effect
    ctx.shadowBlur = 20;
    ctx.shadowColor = `hsla(${180 + w * 60}, 70%, 60%, ${0.2 + normalizedIntensity * 0.3})`;
    ctx.stroke();
    
    ctx.restore();
  }

  // Draw energy pulses from center
  if (normalizedIntensity > 0.6) {
    const pulseCount = 5;
    const centerX = width / 2;
    const centerY = height / 2;
    
    for (let p = 0; p < pulseCount; p++) {
      const pulseRadius = (time * 100 + p * 50) % 200;
      const pulseAlpha = Math.max(0, 1 - pulseRadius / 200) * normalizedIntensity;
      
      if (pulseAlpha > 0) {
        ctx.save();
        ctx.strokeStyle = `hsla(${280 + p * 20}, 80%, 70%, ${pulseAlpha})`;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(centerX, centerY, pulseRadius, 0, Math.PI * 2);
        ctx.stroke();
        ctx.restore();
      }
    }
  }

  // Draw constellation connections
  drawConstellationEffect(ctx, dataArray, width, height, time);
};

const drawConstellationEffect = (ctx, dataArray, width, height, time) => {
  const starCount = 12;
  const stars = [];
  
  // Generate star positions based on audio data
  for (let i = 0; i < starCount; i++) {
    const dataIndex = Math.floor((i / starCount) * dataArray.length);
    const intensity = dataArray[dataIndex] / 255;
    
    const angle = (i / starCount) * Math.PI * 2 + time * 0.1;
    const radius = 100 + intensity * 80;
    
    const star = {
      x: width/2 + Math.cos(angle) * radius,
      y: height/2 + Math.sin(angle) * radius,
      intensity: intensity,
      hue: (i / starCount) * 360 + time * 10
    };
    
    stars.push(star);
    
    // Draw star
    if (intensity > 0.3) {
      ctx.save();
      ctx.fillStyle = `hsla(${star.hue}, 80%, 70%, ${intensity})`;
      ctx.shadowBlur = 10;
      ctx.shadowColor = `hsla(${star.hue}, 80%, 70%, ${intensity * 0.5})`;
      ctx.beginPath();
      ctx.arc(star.x, star.y, 2 + intensity * 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();
    }
  }
  
  // Draw connections between nearby stars
  for (let i = 0; i < stars.length; i++) {
    for (let j = i + 1; j < stars.length; j++) {
      const star1 = stars[i];
      const star2 = stars[j];
      
      const distance = Math.sqrt(
        Math.pow(star1.x - star2.x, 2) + Math.pow(star1.y - star2.y, 2)
      );
      
      if (distance < 150 && star1.intensity > 0.4 && star2.intensity > 0.4) {
        const connectionAlpha = Math.max(0, 1 - distance / 150) * 
                               Math.min(star1.intensity, star2.intensity);
        
        ctx.save();
        ctx.strokeStyle = `hsla(${(star1.hue + star2.hue) / 2}, 70%, 60%, ${connectionAlpha * 0.6})`;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(star1.x, star1.y);
        ctx.lineTo(star2.x, star2.y);
        ctx.stroke();
        ctx.restore();
      }
    }
  }
};

// Export the component function that matches the expected interface
export function CosmicWaves({ ctx, dataArray, width, height, isPlaying }) {
  const time = Date.now() * 0.001; // Convert to seconds for smooth animation
  drawCosmicWaves(ctx, dataArray, width, height, time);
}